/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from "react";
import Header from "./components/Header";
import { Title } from "./components/Title";
import { RevenuePanel } from "./components/RevenuePanel";
import { StakingInput } from "./components/StakingInput";
import { NextStep } from "./components/NextStep";
import { StakingTimeSelection } from "./components/StakingTimeSelection";
import { chain, formatDate, getYearRate, isBitkeep } from "./utils";
import styles from "./page.module.scss";
import { ExecutionPledge, ExecutionStatusEnum } from "./components/ExecutionPledge";
import classNames from "classnames";
import { useExecutionRef } from "./hooks/useExecution";
import { useTitle } from "./hooks/useTItle";
import { buildEvmStakingTxs, getStakingMainWelcome, sendEvmTx } from "@/apis/bgbStaking";
import { showToast } from "@/shares/feedback/showToast";
import { useFailContents } from "./hooks/useDrawerContents";
import { MINI_STAKING_AMOUNT, useStore } from "./StoreProvider";
import { useWallet } from "@/wallet";
import { Contract, providers } from "ethers";
import { contractInfo } from "./contract";
import { maxUint256 } from "viem";
import BigNumber from "bignumber.js";
import dayjs from "dayjs";
import Link from "next/link";
import { EClickType, EPageType, useTracker } from "./hooks/useTracker";
export default function Page() {
    const t = useTranslations();
    const searchParams = useSearchParams();
    const { address, getConnector } = useWallet();
    const { setTimeType, timeType, amountValue, accountData, fetchStakingInfos, changeAmountValue } = useStore();
    const [executionPledge, setExecutionPledge] = useState(false);
    const [stakingList, setStakingList] = useState([]);
    const [successInfo, setSuccessInfo] = useState();
    const [errorInfo, setErrorInfo] = useState();
    const { trackerExpose, trackerClick } = useTracker();
    // const [newOrder, setNewOrder] = useState<StakingOrderInfo>();
    const executionPledgeRef = useExecutionRef(executionPledge);
    // header 信息
    const HeaderInfo = useMemo(()=>{
        return {
            title: t("staking", {
                coin: "BGB"
            })
        };
    }, [
        t
    ]);
    useTitle(HeaderInfo.title);
    const { showMessage, getCodeContent } = useFailContents();
    const fetchStakingMainWelcome = useCallback(async ()=>{
        try {
            const res = await getStakingMainWelcome.fetcher({
                chain
            });
            const { status, data } = res;
            if (status === 0 && !(data === null || data === void 0 ? void 0 : data.code)) {
                const stakingData = data.list;
                stakingData.map((itemStaking)=>{
                    const flexible_staking = itemStaking.flexible_staking;
                    itemStaking.poolList = itemStaking.poolList.map((pool)=>{
                        pool.yearRate = getYearRate();
                        pool.flexible_staking = flexible_staking;
                        return pool;
                    });
                });
                const list = [];
                stakingData.forEach((item)=>{
                    list.push(...item.poolList);
                });
                if (list.length) {
                    setTimeType(list[0].pool_id);
                }
                setStakingList(list);
            } else {
                showMessage(data === null || data === void 0 ? void 0 : data.code);
            }
        } catch (error) {
            showToast("fail", error.message);
            throw error;
        }
    }, [
        setTimeType,
        showMessage
    ]);
    const handleStaking = useCallback(async ()=>{
        try {
            const res = await buildEvmStakingTxs.fetcher({
                chain,
                address,
                pool_id: timeType,
                staking_amount: Number(amountValue)
            });
            const { status, data } = res;
            if (status === 0 && (data === null || data === void 0 ? void 0 : data.code) === 0) {
                const { tx, biz_type, inner_order_id, order_id } = data;
                const connector = getConnector(chain);
                // await this.sendStakingTxs(raw_tx,tx)
                const signTx = await connector.sendTransaction({
                    ...tx,
                    contractName: contractInfo.contractName,
                    contractIcon: contractInfo.contractIcon
                });
                const sendEvmTxRes = await sendEvmTx.fetcher({
                    chain,
                    address,
                    biz_type,
                    inner_order_id,
                    signed_tx: "",
                    tx_hash: signTx,
                    order_id: order_id.toString()
                });
                const { data: sendEvmTxResData, status: sendEvmTxResStatus } = sendEvmTxRes;
                if (sendEvmTxResStatus === 0 && (sendEvmTxResData === null || sendEvmTxResData === void 0 ? void 0 : sendEvmTxResData.code) === 0) {
                    const duration = stakingList.find((item)=>item.pool_id === timeType).duration;
                    const successInfo = {
                        stakeAmount: amountValue.toString(),
                        duration,
                        createFormatDate: formatDate(Date.now()),
                        unStakedFormatDate: formatDate(dayjs(Date.now()).add(Number(duration), "second").valueOf()),
                        txHash: signTx
                    };
                    // 清空输入框
                    changeAmountValue("");
                    fetchStakingInfos();
                    setSuccessInfo(successInfo);
                    executionPledgeRef.current.setExecutionStatus(ExecutionStatusEnum.SUCCESS);
                } else {
                    setErrorInfo({
                        content: getCodeContent(data.code)
                    });
                    executionPledgeRef.current.setExecutionStatus(ExecutionStatusEnum.FAIL);
                }
            } else {
                setErrorInfo({
                    content: getCodeContent(data.code)
                });
                executionPledgeRef.current.setExecutionStatus(ExecutionStatusEnum.FAIL);
            }
        } catch (error) {
            setErrorInfo({
                content: error.message
            });
            executionPledgeRef.current.setExecutionStatus(ExecutionStatusEnum.FAIL);
            throw Error;
        }
    // 提交
    }, [
        address,
        timeType,
        amountValue,
        fetchStakingInfos,
        getConnector,
        changeAmountValue,
        stakingList,
        executionPledgeRef,
        getCodeContent
    ]);
    const approveToken = useCallback(async (to)=>{
        try {
            const connector = getConnector(chain);
            const provider = new providers.Web3Provider(connector.provider);
            const signer = provider.getSigner();
            const contract = new Contract(contractInfo.address, contractInfo.abi, signer);
            // 调用approve方法，并传入交易参数
            const tx = await contract.approve(to, maxUint256);
            if (tx) {
                fetchStakingInfos();
                handleStaking();
            } else {
                setErrorInfo({
                    content: getCodeContent(3335)
                });
                executionPledgeRef.current.setExecutionStatus(ExecutionStatusEnum.FAIL);
            }
        } catch (error) {
            setErrorInfo({
                content: error.message
            });
            executionPledgeRef.current.setExecutionStatus(ExecutionStatusEnum.FAIL);
            throw Error;
        }
    }, [
        handleStaking,
        fetchStakingInfos,
        getConnector,
        executionPledgeRef,
        getCodeContent
    ]);
    const handleSubmit = useCallback(async ()=>{
        trackerClick(EPageType.BGB_STAKING_PAGE, EClickType.STAKING);
        const limit = BigNumber(MINI_STAKING_AMOUNT).gt(amountValue);
        if (limit) {
            showToast("warning", t("mintStakingAmount", {
                amount: MINI_STAKING_AMOUNT
            }));
            return;
        }
        setExecutionPledge(true);
        if (BigNumber(accountData.userApproveAmount).gt(amountValue)) {
            await handleStaking();
        } else {
            await approveToken(accountData.stakingAddress);
        }
    }, [
        handleStaking,
        approveToken,
        accountData,
        amountValue,
        t,
        trackerClick
    ]);
    const handleContinue = useCallback(()=>{
        setExecutionPledge(false);
    }, []);
    useEffect(()=>{
        fetchStakingMainWelcome();
        trackerExpose(EPageType.BGB_STAKING_PAGE);
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isBitkeep && /*#__PURE__*/ _jsx(Header, {
                className: classNames({
                    "bg-[var(--BG2)]": !executionPledge
                }),
                HeaderInfo: HeaderInfo,
                RightIcon: /*#__PURE__*/ _jsx(Link, {
                    href: {
                        pathname: "bgbStaking/FAQ",
                        query: searchParams.toString()
                    },
                    children: /*#__PURE__*/ _jsxs("svg", {
                        width: "24",
                        height: "24",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        children: [
                            /*#__PURE__*/ _jsx("g", {
                                clipPath: "url(#clip0_63389_89663)",
                                children: /*#__PURE__*/ _jsx("path", {
                                    d: "M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20ZM11 15H13V17H11V15ZM13 13.355V14H11V12.5C11 12.2348 11.1054 11.9804 11.2929 11.7929C11.4804 11.6054 11.7348 11.5 12 11.5C12.2841 11.5 12.5623 11.4193 12.8023 11.2673C13.0423 11.1154 13.2343 10.8984 13.3558 10.6416C13.4773 10.3848 13.5234 10.0988 13.4887 9.81684C13.454 9.53489 13.34 9.26858 13.1598 9.04891C12.9797 8.82924 12.7409 8.66523 12.4712 8.57597C12.2015 8.48671 11.912 8.47587 11.6364 8.54471C11.3608 8.61354 11.1104 8.75923 10.9144 8.96482C10.7183 9.1704 10.5847 9.42743 10.529 9.706L8.567 9.313C8.68863 8.70508 8.96951 8.14037 9.38092 7.67659C9.79233 7.2128 10.3195 6.86658 10.9086 6.67332C11.4977 6.48006 12.1275 6.44669 12.7337 6.57661C13.3399 6.70654 13.9007 6.99511 14.3588 7.41282C14.8169 7.83054 15.1559 8.36241 15.3411 8.95406C15.5263 9.54572 15.5511 10.1759 15.4129 10.7803C15.2747 11.3847 14.9785 11.9415 14.5545 12.3939C14.1306 12.8462 13.5941 13.1779 13 13.355Z",
                                    fill: "var(--text-1)"
                                })
                            }),
                            /*#__PURE__*/ _jsx("defs", {
                                children: /*#__PURE__*/ _jsx("clipPath", {
                                    id: "clip0_63389_89663",
                                    children: /*#__PURE__*/ _jsx("rect", {
                                        width: "24",
                                        height: "24",
                                        fill: "var(--text-1)"
                                    })
                                })
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "px4",
                children: [
                    /*#__PURE__*/ _jsx(Title, {
                        className: "".concat(styles.titleBox, " mb-20px")
                    }),
                    /*#__PURE__*/ _jsx(RevenuePanel, {
                        className: "mb-25px"
                    }),
                    /*#__PURE__*/ _jsx(StakingTimeSelection, {
                        className: "mb8",
                        list: stakingList
                    }),
                    /*#__PURE__*/ _jsx(StakingInput, {
                        className: "mb-122px"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(NextStep, {
                onSubmit: handleSubmit,
                className: "fixed bottom-0 left-0"
            }),
            /*#__PURE__*/ _jsx(ExecutionPledge, {
                className: "".concat(classNames({
                    hidden: !executionPledge
                }), " bg-[var(--BG0)]"),
                ref: executionPledgeRef,
                onContinue: handleContinue,
                onRetry: handleContinue,
                successInfo: successInfo,
                errorInfo: errorInfo
            })
        ]
    });
}
